import { useState, useContext, useEffect, useMemo } from 'react';
import { FlexTable } from '@evertrue/sdk';
import { useOpenClose, Modal, Icon } from '@evertrue/et-components';
import TasksToolbar from './tasks-toolbar';
import DeleteModal from './delete-modal';
import useTasks from 'apps/tasks/hooks/use-tasks';
import { PropTypes } from 'base/react-libs';
import { TasksContext } from 'apps/tasks/hooks/tasks-context';
import AddTaskButton from '../create/add-task-button';
import { useTasksNotificationContext } from 'apps/tasks/hooks/tasks-notification-context';
import { useIdentity } from 'base/identity-resolver';
import AddTask from 'apps/tasks/components/create/add-task';
import AddToCalendarModal from 'apps/tasks/components/create/add-to-calendar-modal';
import { trackOutreachAction } from 'apps/outreach/utils/utils';
import { useGlobalDrawerProvider } from 'components/drawer/context/global-drawer-context';
import { getContactEmailAddress } from 'apps/journeys/utils/helpers/utils';
import InteractionForm from 'apps/interactions/components/create/interaction-form';
import { PinnedCommentsContext } from 'apps/profile/contexts/pinned-comments-context';
import ThankviewCTA from 'components/thankview/thankview-recorder-cta';
import { createTaskTableColumns } from './task-table-columns';
import { useTaskSorting } from 'apps/tasks/hooks/use-task-sorting';
import colors from 'base/colors';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';
import { walkCadenceGraph, getActionTypeInfo } from 'apps/cadences-v2/utils/cadence-step-utils';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import { sortGroups } from './tasks-table-grouping-utils';

const TasksTable = ({
  // Props
  tasks = [],
  loading,
  total,
  tab = '',
  userType,
  taskFilters = {},
  contact = {},
  handleTaskSelectionChange,
  bulkSelectedTasks, // Function to refresh table data after bulk actions
  // Pagination props
  setLimit,
  limit,
  offset,
  setOffset,
}) => {
  // ============================================================================
  // HOOKS & CONTEXT
  // ============================================================================
  const { getAndSetGlobalTasksDueCount } = useTasksNotificationContext();
  const { referrer, filtersDefaultValue, filters, updateTableSettings, groupBy } = useContext(TasksContext);
  const { user } = useIdentity();
  const { flagEnabled: cadenceV2Enabled } = useFeatureFlag('cadenceV2Internal');
  const { deleteTask, toggleCompletionStatus, parseActionType } = useTasks();
  const { addDrawer } = useGlobalDrawerProvider();
  const { addNewCommentToState } = useContext(PinnedCommentsContext);

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Task selection state
  const [selectedTask, setSelectedTask] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  // Modal states
  const [isOpen, openModal, closeModal] = useOpenClose();
  const [isAddToCalendarModalOpen, openAddToCalendarModal, closeAddToCalendarModal] = useOpenClose();
  const [isTaskModalOpen, setTaskModalOpen] = useState(false);
  const [isPinnedCommentModalOpen, openPinnedCommentModal, closePinnedCommentModal] = useOpenClose();

  // Other states
  const [taskContact, setTaskContact] = useState();
  // Initialize sortKey based on initial taskFilters
  const [sortKey, setSortKey] = useState(() => {
    return taskFilters?.statuses?.includes('COMPLETED') ? 'completed_at' : 'current_due_date';
  });
  const [sortOrder, setSortOrder] = useState('ASC');

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const updateRowSelectionModel = (newModel) => {
    setRowSelectionModel(newModel);
  };

  const isFilterApplied = () => !(JSON.stringify(filters) === JSON.stringify(filtersDefaultValue));

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  // Task interaction handlers
  const handleTaskNameClick = (task) => {
    // Enrich v2 tasks with cadence name + step number for the modal badge (only when flag is on)
    const cadenceName =
      cadenceV2Enabled && task.task_source === 'cadence_v2' && task.cadence_id
        ? cadenceNameMap[task.cadence_id] || null
        : null;
    const stepInfo = cadenceV2Enabled && task.cadence_event_id ? v2StepMap[task.cadence_event_id] : null;
    if (cadenceName || stepInfo) {
      setSelectedTask({
        ...task,
        _cadenceName: cadenceName || stepInfo?.cadenceName || null,
        _stepNumber: stepInfo?.stepNumber || null,
      });
    } else {
      setSelectedTask(task);
    }
    setTaskModalOpen(true);
  };

  const handleActionTypeClick = (action_type, contact, taskId) => {
    const cAttrs = contact?.contact_attributes;
    const name =
      cAttrs?.name_full?.trim() ||
      [cAttrs?.name_first, cAttrs?.name_last].filter(Boolean).join(' ').trim() ||
      cAttrs?.name_nick?.trim() ||
      null;
    const title = name ?? 'New Message';
    setTaskContact({ ...contact, name });

    if (action_type === 'EMAIL') {
      trackOutreachAction('open_emailer');
      addDrawer('emailer', {
        contact: contact || {},
        taskId,
        name,
        drawerTitle: title,
      });
    } else if (action_type === 'CALL') {
      trackOutreachAction('open_dialer');
      addDrawer('dialer', {
        contact: contact || {},
        phones: (contact && contact.phones) || [],
        name: name,
        drawerTitle: title,
      });
    } else {
      isPinnedCommentModalOpen ? closePinnedCommentModal() : openPinnedCommentModal();
    }
  };

  const handleAddToCalendar = (task) => {
    setSelectedTask(task);
    openAddToCalendarModal();
  };

  const handleDelete = (task) => {
    setSelectedTask(task);
    isOpen ? closeModal() : openModal();
  };

  const handleSort = (field, reverse) => {
    setSortKey(field);
    setSortOrder(reverse ? 'DESC' : 'ASC');
  };

  const handleRowSelectionChange = (newModel) => {
    updateRowSelectionModel(newModel);

    // Convert the new selection model to task objects and update bulk selection
    if (newModel.ids && newModel.ids.size > 0) {
      const selectedTaskIds = Array.from(newModel.ids);
      // Filter out any IDs that don't exist in the current tasks array
      const validTaskIds = selectedTaskIds.filter((id) => tasks.some((task) => task.id === id));
      const selectedTasks = tasks.filter((task) => validTaskIds.includes(task.id));
      handleTaskSelectionChange(selectedTasks);

      // Update the selection model to only include valid IDs
      if (validTaskIds.length !== selectedTaskIds.length) {
        setRowSelectionModel({
          type: 'include',
          ids: new Set(validTaskIds),
        });
      }
    } else {
      handleTaskSelectionChange([]);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    if (taskFilters.statuses.includes('COMPLETED')) {
      setSortKey('completed_at');
    } else {
      // Default to current_due_date for incomplete tasks
      setSortKey('current_due_date');
    }
  }, [taskFilters]);

  useEffect(() => {
    // Safety check: ensure sortKey is never empty
    const safeSortKey = sortKey || 'current_due_date';
    updateTableSettings({ sortProp: safeSortKey, sortReverse: sortOrder === 'ASC' ? false : true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortOrder]);

  // Reset pagination to page 1 when filters change
  useEffect(() => {
    if (offset > 0) {
      setOffset(0);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear row selection when tasks change significantly to prevent stale references
  useEffect(() => {
    const currentTaskIds = new Set(tasks.map((task) => task.id));
    const selectedTaskIds = Array.from(rowSelectionModel.ids || []);

    // If any selected tasks no longer exist in the current tasks array, clear selection
    const hasStaleSelection = selectedTaskIds.some((id) => !currentTaskIds.has(id));

    if (hasStaleSelection) {
      setRowSelectionModel({
        type: 'include',
        ids: new Set(),
      });
      // Also clear the external bulk selection
      handleTaskSelectionChange([]);
    }
  }, [tasks, rowSelectionModel.ids, handleTaskSelectionChange]);

  // ============================================================================
  // CADENCE NAME LOOKUP (v2 tasks don't have journey_name in metadata)
  // ============================================================================

  const v2CadenceIds = useMemo(() => {
    if (!cadenceV2Enabled) return [];
    return [...new Set(tasks.filter((t) => t.task_source === 'cadence_v2' && t.cadence_id).map((t) => t.cadence_id))];
  }, [tasks, cadenceV2Enabled]);

  const [cadenceNameMap, setCadenceNameMap] = useState({});
  // Maps cadence_event_id → { cadenceName, stepNumber, eventTypeLabel } for v2 grouping
  const [v2StepMap, setV2StepMap] = useState({});

  useEffect(() => {
    if (v2CadenceIds.length === 0) {
      setCadenceNameMap({});
      setV2StepMap({});
      return;
    }
    let cancelled = false;
    // TODO: Schema doesn't support filtering cadences by ID list — fetches all and filters client-side.
    // Acceptable for now (orgs typically have <100 cadences). Add cadences(ids:[ID!]) if this becomes slow.
    cadenceGqlCall(`query Cadences {
  cadences {
    id
    name
    events { id eventType sequenceOrder stepLabel }
    edges { id fromCadenceEventId toCadenceEventId edgeType }
  }
}`)
      .then((data) => {
        if (cancelled) return;
        const nameMap = {};
        const stepMap = {};
        (data?.cadences || []).forEach((c) => {
          nameMap[c.id] = c.name;
          // Prefer backend step labels; fall back to graph walk with edges
          const hasBackendLabels = (c.events || []).some((e) => e.stepLabel != null);
          if (hasBackendLabels) {
            (c.events || []).forEach((ev) => {
              if (!ev.stepLabel) return; // skip start/end/wait/condition events
              const info = getActionTypeInfo(ev.eventType);
              stepMap[ev.id] = {
                cadenceName: c.name,
                cadenceId: c.id,
                stepNumber: ev.stepLabel,
                eventTypeLabel: info.label,
              };
            });
          } else {
            const { items } = walkCadenceGraph(c.events || [], c.edges || []);
            items
              .filter((item) => item.type === 'step')
              .forEach((step) => {
                stepMap[step.id] = {
                  cadenceName: c.name,
                  cadenceId: c.id,
                  stepNumber: String(step.num) + (step.suffix || ''),
                  eventTypeLabel: step.actionType,
                };
              });
          }
        });
        setCadenceNameMap(nameMap);
        setV2StepMap(stepMap);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [v2CadenceIds.length]);

  // ============================================================================
  // DERIVED STATE & HOOKS
  // ============================================================================

  const { sortModel, handleSortModelChange } = useTaskSorting({
    sortProp: sortKey,
    sortReverse: sortOrder === 'DESC',
    onSort: handleSort,
  });

  // Track which groups are collapsed — empty set = nothing collapsed = all expanded
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Reset collapsed state when grouping changes (restores all groups to expanded)
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupBy]);

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  /**
   * When groupBy is 'cadence_step', build a flat array with synthetic group header rows
   * interspersed before each group of tasks. Groups are ordered cadence-first (alphabetically
   * by cadence name), then by step label within each cadence. Non-cadence tasks always appear last.
   *
   * Routing rules (in priority order):
   *   1. task_source === 'cadence_v2' AND cadence_event_id is truthy → cadence v2 step group
   *   2. task_source === 'cadence_v2' AND cadence_event_id is falsy  → ungrouped (Non-Cadence Tasks)
   *   3. metadata.journey_id AND metadata.step_number are truthy     → cadence v1 step group
   *   4. All other tasks                                             → ungrouped (Non-Cadence Tasks)
   *
   * Rule 2 is explicit: a v2 task with no cadence_event_id is routed directly to ungrouped
   * without being tested against the v1 metadata path. This prevents accidental routing if
   * a v2 task ever carries legacy journey_id metadata keys.
   */
  const groupedRows = useMemo(() => {
    if (groupBy !== 'cadence_step') return null;

    // cadenceGroups: Map<cadenceId, { cadenceName, steps: Map<stepKey, group> }>
    const cadenceGroups = new Map();
    // ungrouped bucket for tasks with no cadence association — renders last after all cadence groups
    const ungrouped = { key: 'ungrouped', label: 'Non-Cadence Tasks', tasks: [] };

    (tasks || []).forEach((task) => {
      const meta = task.metadata || {};

      if (task.task_source === 'cadence_v2') {
        // v2 tasks: only group by cadence step when cadence_event_id is present.
        // A v2 task with a null/missing cadence_event_id falls into the ungrouped bucket
        // rather than being tested against the v1 metadata path, preventing any accidental
        // routing if a v2 task ever carries legacy journey_id metadata.
        if (task.cadence_event_id) {
          const stepInfo = v2StepMap[task.cadence_event_id];
          const cadenceId = stepInfo?.cadenceId ?? task.cadence_id ?? 'unknown';
          const cadenceName = stepInfo?.cadenceName ?? cadenceNameMap[task.cadence_id] ?? 'Cadence';
          const groupKey = `v2-${cadenceId}-event-${task.cadence_event_id}`;
          const stepLabel = stepInfo
            ? `${stepInfo.cadenceName} \u2013 Step ${stepInfo.stepNumber}: ${stepInfo.eventTypeLabel}`
            : `${cadenceName} \u2013 Step (loading...)`;
          const stepSortKey = stepInfo?.stepNumber ?? '';

          if (!cadenceGroups.has(cadenceId)) {
            cadenceGroups.set(cadenceId, { cadenceName, steps: new Map() });
          }
          const cadenceEntry = cadenceGroups.get(cadenceId);
          if (!cadenceEntry.steps.has(groupKey)) {
            cadenceEntry.steps.set(groupKey, { key: groupKey, label: stepLabel, stepSortKey, tasks: [] });
          }
          cadenceEntry.steps.get(groupKey).tasks.push(task);
        } else {
          // cadence_v2 task with no cadence_event_id → non-cadence bucket
          ungrouped.tasks.push(task);
        }
      } else if (meta.journey_id && meta.step_number) {
        const cadenceId = `v1-${meta.journey_id}`;
        const cadenceName = meta.journey_name || 'Cadence';
        const groupKey = `v1-${meta.journey_id}-step-${meta.step_number}`;
        const stepLabel = `${cadenceName} \u2013 Step ${meta.step_number}${meta.day_number ? ` | Day ${meta.day_number}` : ''}`;
        const stepSortKey = String(meta.step_number);

        if (!cadenceGroups.has(cadenceId)) {
          cadenceGroups.set(cadenceId, { cadenceName, steps: new Map() });
        }
        const cadenceEntry = cadenceGroups.get(cadenceId);
        if (!cadenceEntry.steps.has(groupKey)) {
          cadenceEntry.steps.set(groupKey, { key: groupKey, label: stepLabel, stepSortKey, tasks: [] });
        }
        cadenceEntry.steps.get(groupKey).tasks.push(task);
      } else {
        // All remaining tasks (non-cadence source, no v1 journey metadata) → ungrouped
        ungrouped.tasks.push(task);
      }
    });

    // Sort cadences alphabetically by name, then steps within each cadence by step label.
    // Non-cadence tasks always go last.
    const sorted = sortGroups(cadenceGroups, ungrouped);

    const rows = [];
    sorted.forEach((group) => {
      const isExpanded = !collapsedGroups.has(group.key);
      rows.push({
        id: `group-header-${group.key}`,
        _isGroupHeader: true,
        _groupKey: group.key,
        _groupLabel: group.label,
        _groupCount: group.tasks.length,
        _isExpanded: isExpanded,
      });
      // Only include task rows if the group is expanded
      if (isExpanded) {
        rows.push(...group.tasks);
      }
    });
    return rows;
  }, [tasks, groupBy, v2StepMap, cadenceNameMap, collapsedGroups]);

  const isGrouped = groupBy === 'cadence_step';
  const displayRows = isGrouped && groupedRows ? groupedRows : tasks || [];

  // Create columns using the extracted function
  const columns = createTaskTableColumns({
    user,
    taskFilters,
    cadenceNameMap: cadenceV2Enabled ? cadenceNameMap : {},
    cadenceV2Enabled,
    onTaskNameClick: handleTaskNameClick,
    onActionTypeClick: handleActionTypeClick,
    onAddToCalendar: handleAddToCalendar,
    onDelete: handleDelete,
    toggleCompletionStatus,
    parseActionType,
    trackOutreachAction,
    addDrawer,
    openPinnedCommentModal,
    closePinnedCommentModal,
    isPinnedCommentModalOpen,
    ThankviewCTA,
    getContactEmailAddress,
  });

  // Filter columns based on referrer and tab
  const baseFilteredColumns =
    referrer === 'contact'
      ? columns.filter((col) => col.field !== 'target_contact_name')
      : tab === 'assigned'
        ? columns.filter((col) => col.field !== 'creator_user_name')
        : tab !== 'assigned'
          ? columns.filter((col) => col.field !== 'owner_user_name')
          : columns;

  // Wrap columns to render group header rows when grouped.
  // The first non-checkbox column gets colSpan equal to the total column count so the
  // group header stretches edge-to-edge like a single bar.
  const filteredColumns = isGrouped
    ? baseFilteredColumns.map((col, colIdx) => {
        const isFirstDataCol = colIdx === 0; // first column after checkbox (checkbox is separate)
        return {
          ...col,
          colSpan: (value, row) => {
            if (!row._isGroupHeader) return 1;
            // First data column spans the entire row; remaining columns are consumed by the span.
            return isFirstDataCol ? baseFilteredColumns.length : 1;
          },
          renderCell: (params) => {
            if (params.row._isGroupHeader) {
              if (isFirstDataCol) {
                const expanded = params.row._isExpanded;
                return (
                  <button
                    type="button"
                    className="tasks-group-header-cell"
                    onClick={() => toggleGroupCollapse(params.row._groupKey)}
                  >
                    <span className={`tasks-group-header-cell--chevron ${expanded ? 'is-expanded' : ''}`}>
                      <Icon icon="chevron-right" />
                    </span>
                    <span className="tasks-group-header-cell--label">{params.row._groupLabel}</span>
                    <span className="tasks-group-header-cell--count">
                      {params.row._groupCount} {params.row._groupCount === 1 ? 'task' : 'tasks'}
                    </span>
                  </button>
                );
              }
              return null;
            }
            return col.renderCell?.(params) ?? params.formattedValue ?? params.value;
          },
        };
      })
    : baseFilteredColumns;

  // ============================================================================
  // RENDER
  // ============================================================================

  const emptyRowMessage = isFilterApplied() ? (
    <>
      <Icon icon="searchlist" size={3} style={{ color: colors.gray500 }} />
      <h1> You don't have any tasks matching your search criteria.</h1>
      <p>Try adjusting your filters or changing your search combination.</p>
    </>
  ) : (
    <>
      <Icon icon="tasks" iconKit="gt2" size={3} style={{ color: colors.gray500 }} />
      <h1>All Done!</h1>
      <p>Your Task List is Empty</p>
      <AddTaskButton primary={true} userType={userType} contact={contact} />
    </>
  );

  return (
    <>
      <div>
        {/* Toolbar */}
        <TasksToolbar numTasks={total} />
        <div className="tasks-lists-container">
          <div
            className="tasks-lists-table"
            style={{
              height: 'calc(100vh - 290px)',
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <FlexTable
              key={`tasks-table-${displayRows.length}-${groupBy}-${JSON.stringify(
                tasks.map((t) => ({ id: t.id, completed_at: t.completed_at }))
              )}`}
              tableKey="tasks-table"
              columns={filteredColumns}
              rows={displayRows}
              getRowId={(params) => params.id}
              // Table configuration
              checkboxSelection
              rowSelection
              keepNonExistentRowsSelected
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionChange}
              // Row selection logic
              isRowSelectable={(params) => {
                if (params.row._isGroupHeader) return false;
                const task = params.row;
                if (!task || !task.id) return false;
                const taskExists = tasks.some((t) => t.id === task.id);
                if (!taskExists) return false;
                return task.owner_user_id === user.id || task.creator_user_id === user.id;
              }}
              getRowClassName={(params) => (params.row._isGroupHeader ? 'tasks-group-header-row' : '')}
              getRowHeight={(params) => (params.row?._isGroupHeader ? 44 : 'auto')}
              slots={{
                noRowsOverlay: () => <div className="table-empty-state--container">{emptyRowMessage}</div>,
              }}
              // Data & loading
              rowCount={isGrouped ? displayRows.length : total}
              loading={loading}
              // Sorting — disable when grouped since groups define the order
              sortModel={isGrouped ? [] : sortModel}
              onSortModelChange={isGrouped ? undefined : handleSortModelChange}
              // Column menu & styling
              disableColumnMenu={isGrouped}
              showColumnVerticalBorder={false}
              autosizeOptions={{
                columns: ['title'],
                includeOutliers: true,
                includeHeaders: true,
                expand: false,
              }}
              // Pagination — disable when grouped to show all results
              {...(isGrouped
                ? {
                    paginationModel: { page: 0, pageSize: Math.min(displayRows.length || 25, 100) },
                    hideFooter: displayRows.length <= 100,
                    pageSizeOptions: [100],
                  }
                : {
                    paginationModel: {
                      page: Math.floor(offset / Math.min(limit, 100)),
                      pageSize: Math.min(limit, 100),
                    },
                    onPaginationModelChange: ({ page, pageSize }) => {
                      setLimit(pageSize);
                      setOffset(page * pageSize);
                    },
                    pageSizeOptions: [10, 25, 50, 100],
                  })}
            />
          </div>
        </div>
      </div>

      {/* ============================================================================
          MODALS
          ============================================================================ */}

      {/* Delete Task Modal */}
      <DeleteModal
        title="Delete Task"
        body="Are you sure you want to delete this task?"
        isOpen={isOpen}
        onClose={closeModal}
        onSubmit={async () => {
          await deleteTask(selectedTask, userType);
          getAndSetGlobalTasksDueCount();
        }}
      />

      {/* Add to Calendar Modal */}
      {isAddToCalendarModalOpen && (
        <AddToCalendarModal
          isOpen={isAddToCalendarModalOpen}
          closeModal={closeAddToCalendarModal}
          calendarParams={selectedTask}
          showTaskSaved={false}
        />
      )}

      {/* Add/Edit Task Modal */}
      <AddTask
        isOpen={isTaskModalOpen}
        closeModal={() => setTaskModalOpen(false)}
        handleClose={() => setTaskModalOpen(false)}
        taskObj={selectedTask}
      />

      {/* Pinned Comment Modal */}
      <Modal isOpen={isPinnedCommentModalOpen} closeModal={closePinnedCommentModal}>
        <InteractionForm
          contact={taskContact}
          referrer="add-pinned-comment-action"
          interaction={{ interaction_type: 'EverTrue Comment', pinned: true }}
          requestHide={closePinnedCommentModal}
          addNewCommentToState={addNewCommentToState}
        />
      </Modal>
    </>
  );
};

// ============================================================================
// PROPTYPES
// ============================================================================

TasksTable.propTypes = {
  // Data props
  loading: PropTypes.bool,
  tasks: PropTypes.array,
  total: PropTypes.number,
  tab: PropTypes.string,
  userType: PropTypes.string,
  taskFilters: PropTypes.object,
  contact: PropTypes.object,

  // Selection props
  bulkSelectedTasks: PropTypes.array,
  handleTaskSelectionChange: PropTypes.func,

  // Pagination props
  setLimit: PropTypes.func.isRequired,
  limit: PropTypes.number.isRequired,
  offset: PropTypes.number.isRequired,
  setOffset: PropTypes.func.isRequired,
};

export default TasksTable;
